import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getApiOrigin } from '../utils/apiBaseUrl';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, isPreviewMode } = useAuth();
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    // Skip socket connection in preview mode
    if (isPreviewMode) {
      console.log('[Socket] Skipping connection in preview mode');
      return;
    }
    
    if (user && user.mode === 'college') {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token found for socket connection');
        return;
      }

      // Create socket connection
      const socketUrl = getApiOrigin();
      console.log('Socket URL:', socketUrl);
      console.log('With token:', token ? 'Token exists' : 'No token');
      
      const newSocket = io(socketUrl, {
        auth: {
          token
        },
        transports: ['polling', 'websocket'],
        withCredentials: false,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
        timeout: 10000
      });

      newSocket.on('connect', () => {
        console.log('✅ Socket connected successfully!');
        console.log('Socket ID:', newSocket.id);
        console.log('User:', user.name);
        console.log('User ID:', user._id);
        setConnected(true);
      });

      newSocket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
        console.error('Error details:', error);
        setConnected(false);
      });

      newSocket.on('disconnect', (reason) => {
        console.log('Socket disconnected. Reason:', reason);
        setConnected(false);
      });

      // Handle initial online users list
      newSocket.on('online_users_list', (data) => {
        console.log('Received online users list:', data.userIds);
        setOnlineUsers(new Set(data.userIds));
      });

      // Handle online/offline users
      newSocket.on('user_online', (data) => {
        console.log('User came online:', data.userId, data.name);
        setOnlineUsers(prev => new Set([...prev, data.userId]));
      });

      newSocket.on('user_offline', (data) => {
        console.log('User went offline:', data.userId);
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      // Disconnect socket if user logs out or not in college mode
      if (socket) {
        socket.close();
        setSocket(null);
        setConnected(false);
      }
    }
  }, [user]);

  const sendMessage = useCallback((receiverId, content) => {
    if (socket && connected) {
      socket.emit('send_message', { receiverId, content });
    } else {
      console.error('Socket not connected');
    }
  }, [socket, connected]);

  const markAsRead = useCallback((senderId) => {
    if (socket && connected) {
      socket.emit('mark_as_read', { senderId });
    }
  }, [socket, connected]);

  const startTyping = useCallback((receiverId) => {
    if (socket && connected) {
      socket.emit('typing_start', { receiverId });
    }
  }, [socket, connected]);

  const stopTyping = useCallback((receiverId) => {
    if (socket && connected) {
      socket.emit('typing_stop', { receiverId });
    }
  }, [socket, connected]);

  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  // Group chat methods
  const joinGroupRoom = useCallback((groupId) => {
    if (socket && connected) {
      socket.emit('join_group_room', { groupId });
    } else {
      console.error('Socket not connected');
    }
  }, [socket, connected]);

  const leaveGroupRoom = useCallback((groupId) => {
    if (socket && connected) {
      socket.emit('leave_group_room', { groupId });
    }
  }, [socket, connected]);

  const sendGroupMessage = useCallback((groupId, content) => {
    if (socket && connected) {
      socket.emit('send_group_message', { groupId, content });
    } else {
      console.error('Socket not connected');
    }
  }, [socket, connected]);

  const startGroupTyping = useCallback((groupId) => {
    if (socket && connected) {
      socket.emit('group_typing_start', { groupId });
    }
  }, [socket, connected]);

  const stopGroupTyping = useCallback((groupId) => {
    if (socket && connected) {
      socket.emit('group_typing_stop', { groupId });
    }
  }, [socket, connected]);

  const value = {
    socket,
    connected,
    onlineUsers,
    sendMessage,
    markAsRead,
    startTyping,
    stopTyping,
    isUserOnline,
    // Group methods
    joinGroupRoom,
    leaveGroupRoom,
    sendGroupMessage,
    startGroupTyping,
    stopGroupTyping
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
